import type { DeepResearchCitationCheck, DeepResearchConfidenceSummary, DeepResearchRoleResult, DeepResearchSource } from './types';

export function computeDeepResearchConfidence(params: {
  sources: DeepResearchSource[];
  citationCheck: DeepResearchCitationCheck;
  roleResults: DeepResearchRoleResult[];
  limitations: string[];
}): DeepResearchConfidenceSummary {
  const { sources, citationCheck, roleResults, limitations } = params;
  const validSources = sources.filter((source) => source.valid);
  const averageSourceQuality = validSources.length
    ? validSources.reduce((total, source) => total + source.qualityScore, 0) / validSources.length
    : 0;
  const failedRoles = roleResults.filter((result) => result.status === 'failed').length;
  const completeRoles = roleResults.filter((result) => result.status === 'complete').length;
  const completionRatio = roleResults.length ? completeRoles / roleResults.length : 0;

  const rawScore =
    averageSourceQuality * 0.45 +
    citationCheck.coverageScore * 0.35 +
    completionRatio * 100 * 0.2 -
    failedRoles * 8 -
    limitations.length * 4;

  const score = Math.max(5, Math.min(99, Math.round(rawScore)));
  const label = score >= 80 ? 'high' : score >= 55 ? 'medium' : 'low';
  const factors = [
    `${validSources.length} validated source${validSources.length === 1 ? '' : 's'}`,
    `Citation coverage ${citationCheck.coverageScore}%`,
  ];

  if (failedRoles > 0) {
    factors.push(`${failedRoles} agent stage${failedRoles === 1 ? '' : 's'} failed or degraded`);
  }

  if (limitations.length > 0) {
    factors.push(`${limitations.length} limitation${limitations.length === 1 ? '' : 's'} lowered confidence`);
  }

  return {
    score,
    label,
    factors,
  };
}
